export const divergentColorScale = [
  "#00876c",
  "#64ad73",
  "#afd17c",
  "#fff18f",
  "#fbb862",
  "#ee7d4f",
  "#d43d51",
] as const;

type RGBColor = string; // e.g., "#00876c"
type Range = [number, number];

export function scaleLinear(range: Range, colors: readonly RGBColor[]): (value: number) => RGBColor {
    const [min, max] = range;
    const colorCount = colors.length;

    // Ensure the range is valid
    if (min >= max) {
        throw new Error("Invalid range: min must be less than max.");
    }

    // Ensure there are at least two colors for interpolation
    if (colorCount < 2) {
        throw new Error("At least two colors are required for interpolation.");
    }

    // Return a function that calculates the color for a given value
    return (value: number): RGBColor => {
        // Cap the value to the range boundaries
        const cappedValue = Math.min(Math.max(value, min), max);

        // Calculate the position of the value in the range (0 to 1)
        const t = (cappedValue - min) / (max - min);

        // Calculate the index of the lower and upper colors
        const lowerIndex = Math.floor(t * (colorCount - 1));
        const upperIndex = Math.ceil(t * (colorCount - 1));

        // If the value is exactly at a color point, return that color
        if (lowerIndex === upperIndex) {
            return colors[lowerIndex];
        }

        // Interpolate between the two nearest colors
        const lowerColor = colors[lowerIndex];
        const upperColor = colors[upperIndex];
        const interpolationFactor = (t * (colorCount - 1)) - lowerIndex;

        return interpolateColor(lowerColor, upperColor, interpolationFactor);
    };
}

// Helper function to interpolate between two RGB colors
function interpolateColor(color1: RGBColor, color2: RGBColor, factor: number): RGBColor {
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);

    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
